<template>
  <div class="homepage-light">
    <site-header />

    <section class="contact-section">
      <div class="container">
        <div class="contact-grid">

          <div class="contact-info">
            <span class="section-label">{{ $t('nav.contact') }}</span>
            <h1>{{ $t('contact.title') }}</h1>
            <p class="contact-desc">
              {{ $t('contact.subtitle') }}
            </p>
          </div>

          <div class="contact-form-wrap">
            <form
              class="contact-form"
              @submit.prevent="submitForm"
            >
              <div class="form-group">
                <label for="contact-name">{{ $t('contact.name') }}</label>
                <input
                  id="contact-name"
                  v-model="form.name"
                  type="text"
                  :placeholder="$t('contact.yourName')"
                  required
                >
              </div>

              <div class="form-group">
                <label for="contact-firstname">{{ $t('contact.firstName') }}</label>
                <input
                  id="contact-firstname"
                  v-model="form.firstName"
                  type="text"
                  :placeholder="$t('contact.yourFirstName')"
                  required
                >
              </div>

              <div class="form-group">
                <label for="contact-email">{{ $t('contact.email') }}</label>
                <input
                  id="contact-email"
                  v-model="form.email"
                  type="email"
                  :placeholder="$t('contact.emailPlaceholder')"
                  required
                >
              </div>

              <div class="form-group">
                <label for="contact-message">{{ $t('contact.message') }}</label>
                <textarea
                  id="contact-message"
                  v-model="form.message"
                  rows="6"
                  :placeholder="$t('contact.messagePlaceholder')"
                  required
                />
              </div>

              <div class="form-check">
                <input
                  id="contact-agree"
                  v-model="form.agree"
                  type="checkbox"
                  required
                >
                <label for="contact-agree">
                  {{ $t('contact.privacyAccept') }} <a
                    href="/politique-confidentialite"
                    @click.prevent="$router.push('/politique-confidentialite')"
                  >{{ $t('contact.privacyPolicy') }}</a>.
                </label>
              </div>

              <button
                type="submit"
                class="submit-btn"
                :disabled="sending"
              >
                <span v-if="!sending">{{ $t('contact.send') }}</span>
                <span v-else>{{ $t('contact.sending') }}</span>
              </button>

              <transition name="fade">
                <div
                  v-if="success"
                  class="form-success"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#22c55e"
                    stroke-width="2.4"
                  ><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                  {{ $t('contact.success') }}
                </div>
              </transition>
              <transition name="fade">
                <div
                  v-if="errorMsg"
                  class="form-error"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ef4444"
                    stroke-width="2.4"
                  ><circle
                    cx="12"
                    cy="12"
                    r="10"
                  /><line
                    x1="15"
                    y1="9"
                    x2="9"
                    y2="15"
                  /><line
                    x1="9"
                    y1="9"
                    x2="15"
                    y2="15"
                  /></svg>
                  {{ errorMsg }}
                </div>
              </transition>
            </form>
          </div>

        </div>
      </div>
    </section>

    <page-footer />
  </div>
</template>

<script>
import axios from 'axios'
import siteHeader from '@/views/home/HomePage2/components/SiteHeader.vue'
import pageFooter from '@/views/home/HomePage2/components/pageFooter.vue'

export default {
  name: 'ContactPage',
  components: { siteHeader, pageFooter },
  data() {
    return {
      form: {
        firstName: '', name: '', email: '', message: '', agree: false,
      },
      sending: false,
      success: false,
      errorMsg: '',
    }
  },
  mounted() {
    document.title = "Contact | Minds' Academy"
  },
  beforeDestroy() {
    document.title = "Minds' Academy"
  },
  methods: {
    async submitForm() {
      if (!this.form.agree) return
      this.sending = true
      this.errorMsg = ''
      try {
        const baseURL = process.env.VUE_APP_API_URL || ''
        await axios.post(`${baseURL}/contact/send`, {
          firstName: this.form.firstName,
          name: this.form.name,
          email: this.form.email,
          message: this.form.message,
        })
        this.success = true
        this.form = {
          firstName: '', name: '', email: '', message: '', agree: false,
        }
        setTimeout(() => { this.success = false }, 6000)
      } catch (err) {
        this.errorMsg = err.response?.data?.error || this.$t('contact.error')
        setTimeout(() => { this.errorMsg = '' }, 5000)
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style scoped>
.homepage-light {
  --teal: #18b9a7;
  --teal-dark: #0d756c;
  --coral: #ff6f59;
  --coral-soft: #ff8a72;
  --cream: #fffaf3;
  --ink-deep: #100707;
  background: var(--cream);
}

.contact-section {
  padding: 64px 0 80px;
  background:
    radial-gradient(700px 340px at 20% 20%, rgba(24,185,167,0.14) 0%, rgba(255,255,255,0) 65%),
    radial-gradient(520px 300px at 82% 72%, rgba(255,111,89,0.12) 0%, rgba(255,255,255,0) 65%),
    linear-gradient(180deg, var(--cream) 0%, #fff6ee 52%, var(--cream) 100%);
}

.container {
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 24px;
}

.contact-grid {
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 64px;
  align-items: start;
}

.section-label {
  display: inline-block;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--teal-dark);
  background: rgba(24, 185, 167, 0.12);
  border: 1px solid rgba(24, 185, 167, 0.22);
  padding: 6px 16px;
  border-radius: 50px;
  margin-bottom: 16px;
}

.contact-info h1 {
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 800;
  color: var(--ink-deep);
  line-height: 1.15;
  margin: 0 0 16px;
  letter-spacing: -0.03em;
}

.contact-desc {
  font-size: 16px;
  color: var(--muted);
  line-height: 1.7;
  margin: 0 0 36px;
}

.info-items {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.info-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 12px;
  background: rgba(24, 185, 167, 0.12);
  border: 1px solid rgba(24, 185, 167, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
}
.info-icon svg {
  width: 20px;
  height: 20px;
  color: var(--teal-dark);
}

.info-link {
  font-size: 15px;
  font-weight: 600;
  color: var(--teal-dark) !important;
  text-decoration: none;
  line-height: 44px;
}
.info-link:hover {
  color: var(--coral) !important;
}

.info-text {
  font-size: 15px;
  font-weight: 500;
  color: var(--muted);
  line-height: 1.6;
  padding-top: 8px;
}

/* Form */
.contact-form-wrap {
  background: #ffffff;
  border: 1px solid rgba(24, 185, 167, 0.20);
  border-radius: 20px;
  padding: 36px;
  box-shadow: 0 22px 60px rgba(13, 117, 108, 0.12);
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group label {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 12px;
  font-family: inherit;
  font-size: 15px;
  color: var(--ink);
  background: rgba(15, 23, 42, 0.02);
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(71, 85, 105, 0.5);
}

.form-group input:focus,
.form-group textarea:focus {
  border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(24, 185, 167, 0.16);
}

.form-group textarea {
  resize: vertical;
  min-height: 120px;
}

.form-check {
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-check input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: var(--teal);
  cursor: pointer;
  flex-shrink: 0;
}

.form-check label {
  font-size: 13px;
  color: var(--muted);
  cursor: pointer;
}

.form-check a {
  color: var(--teal-dark) !important;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.submit-btn {
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--coral) 0%, var(--coral-soft) 100%);
  color: #fff;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.2s, transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 12px 28px rgba(255, 111, 89, 0.30);
}
.submit-btn:hover:not(:disabled) {
  filter: brightness(1.04);
  transform: translateY(-2px);
  box-shadow: 0 16px 36px rgba(255, 111, 89, 0.38);
}
.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.form-success {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.22);
  font-size: 14px;
  font-weight: 600;
  color: #166534;
}
.form-success svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.form-error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.22);
  font-size: 14px;
  font-weight: 600;
  color: #991b1b;
}
.form-error svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

@media (max-width: 900px) {
  .contact-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .contact-section { padding: 44px 0 60px; }
}

@media (max-width: 600px) {
  .contact-form-wrap { padding: 24px; }
  .contact-info h1 { font-size: 28px; }
}
</style>
